import React, { useState } from 'react';
import { UserProfile } from '../../types';
import { MessageSquare, Save, Image as ImageIcon, PaintBucket } from 'lucide-react';
import { soundEffects } from '../../lib/soundEffects';

interface Props {
  currentUser: UserProfile;
  onUpdateProfile: (profile: Partial<UserProfile>) => void;
}

export const ChatSettingsSection: React.FC<Props> = ({ currentUser, onUpdateProfile }) => {
  const [chatSettings, setChatSettings] = useState<NonNullable<UserProfile['chatSettings']>>(
    currentUser.chatSettings || {
      theme: 'dark',
      fontSize: 14,
      background: 'default',
      messagePreviews: true,
      autoPlayMedia: true,
      saveMediaToGallery: false,
      autoDeleteTimer: 0,
    }
  );

  const [isSaving, setIsSaving] = useState(false);
  const [savedSuccess, setSavedSuccess] = useState(false);

  const handleSave = async () => {
    soundEffects.playTap();
    setIsSaving(true);
    await onUpdateProfile({ chatSettings });
    setIsSaving(false);
    setSavedSuccess(true);
    setTimeout(() => setSavedSuccess(false), 2000);
  };

  const updateSettings = (key: keyof typeof chatSettings, val: any) => {
    setChatSettings({ ...chatSettings, [key]: val });
  };

  const renderToggle = (label: string, key: keyof typeof chatSettings, description?: string) => (
    <div className="flex items-center justify-between p-3 bg-[#141c2a] rounded-xl border border-slate-800">
      <div>
        <div className="text-sm font-semibold text-slate-200">{label}</div>
        {description && <div className="text-[10px] text-slate-400 mt-1 max-w-[200px]">{description}</div>}
      </div>
      <button
        onClick={() => updateSettings(key, !chatSettings[key])}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${chatSettings[key] ? 'bg-indigo-500' : 'bg-slate-700'}`}
      >
        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${chatSettings[key] ? 'translate-x-1' : '-translate-x-6'}`} />
      </button>
    </div>
  );

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
      <div className="bg-[#0e1422] rounded-3xl p-5 border border-slate-800/80 shadow-lg">
        <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-indigo-400" />
          إعدادات المحادثات
        </h3>

        <div className="space-y-3">
          <div className="p-3 bg-[#141c2a] rounded-xl border border-slate-800">
            <div className="flex justify-between items-center mb-3">
              <span className="text-sm font-semibold text-slate-200">حجم الخط في المحادثات</span>
              <span className="text-xs font-bold text-indigo-400">{chatSettings.fontSize}px</span>
            </div>
            <input 
              type="range" 
              min="12" max="24" 
              value={chatSettings.fontSize} 
              onChange={(e) => updateSettings('fontSize', parseInt(e.target.value))}
              className="w-full accent-indigo-500 h-1.5 bg-slate-800 rounded-lg appearance-none cursor-pointer"
            />
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-3 bg-[#141c2a] rounded-xl border border-slate-800">
            <div className="text-sm font-semibold text-slate-200">مظهر المحادثة</div>
            <select
              value={chatSettings.theme}
              onChange={(e) => updateSettings('theme', e.target.value)}
              className="bg-[#0e1422] border border-slate-700 text-indigo-400 text-xs rounded-lg px-2 py-1.5 outline-none focus:border-indigo-500"
            >
              <option value="system">النظام</option>
              <option value="light">فاتح (Light)</option>
              <option value="dark">داكن (Dark)</option>
              <option value="dim">خافت (Dim)</option>
            </select>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-3 bg-[#141c2a] rounded-xl border border-slate-800">
            <div className="text-sm font-semibold text-slate-200">مؤقت الحذف التلقائي للرسائل</div>
            <select
              value={chatSettings.autoDeleteTimer}
              onChange={(e) => updateSettings('autoDeleteTimer', parseInt(e.target.value))}
              className="bg-[#0e1422] border border-slate-700 text-indigo-400 text-xs rounded-lg px-2 py-1.5 outline-none focus:border-indigo-500"
            >
              <option value={0}>معطل</option>
              <option value={86400}>بعد يوم واحد</option>
              <option value={604800}>بعد أسبوع</option>
              <option value={2592000}>بعد شهر</option>
            </select>
          </div>

          {renderToggle('التشغيل التلقائي للوسائط', 'autoPlayMedia')}
          {renderToggle('حفظ الوسائط في المعرض', 'saveMediaToGallery', 'يتم حفظ الصور والفيديوهات تلقائياً في جهازك.')}
          {renderToggle('معاينة الرسائل', 'messagePreviews', 'عرض سطر من الرسالة في قائمة المحادثات.')}

        </div>

        <button disabled={isSaving} onClick={handleSave} className={`mt-4 w-full py-2.5 font-bold rounded-xl flex items-center justify-center gap-2 transition active:scale-95 ${savedSuccess ? 'bg-indigo-400 text-white' : 'bg-indigo-500 hover:bg-indigo-400 text-white disabled:opacity-50'}`}>
          <Save className="w-4 h-4" />
          {isSaving ? 'جاري الحفظ...' : savedSuccess ? 'تم الحفظ' : 'حفظ إعدادات المحادثة'}
        </button>
      </div>
    </div>
  );
};
